import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { Btn } from '../components/Btn';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { Ic } from '../components/Icon';
import { Logo } from '../components/Logo';
import { useAuth } from '../store/auth';

export function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAuth((s) => s.token);

  const info = useQuery({
    // include auth token in key so anon vs authenticated views don't share cache
    // (viewerIsMember zmienia się po loginie)
    queryKey: ['invitation', code, !!token],
    queryFn: () => authApi.invitationInfo(code!),
    enabled: !!code,
    refetchOnMount: 'always',
  });

  const accept = useMutation({
    mutationFn: () => authApi.acceptInvitation(code!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
      navigate(info.data?.roomId ? `/rooms/${info.data.roomId}` : '/', { replace: true });
    },
  });

  if (info.isLoading) {
    return <CenteredMessage>Ładowanie zaproszenia…</CenteredMessage>;
  }

  if (info.isError || !info.data) {
    return (
      <CenteredMessage>
        <h2 className="serif" style={{ fontSize: 28, marginBottom: 8 }}>
          Zaproszenie niedostępne
        </h2>
        <p style={{ color: '#5b6578', marginBottom: 24 }}>
          Kod może być nieprawidłowy lub wygasły.
        </p>
        <Link to="/login" style={{ color: '#0f1e3d', fontWeight: 600, borderBottom: '1px solid' }}>
          Wróć do logowania
        </Link>
      </CenteredMessage>
    );
  }

  const inv = info.data;
  const expired = inv.expired;
  const used = inv.used;
  const isRoomInvite = !!inv.roomId;
  const alreadyMember = isRoomInvite && inv.viewerIsMember === true;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fbfaf6',
        padding: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ maxWidth: 520, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <Logo size={80} />
          </div>
          <Pill color="accent" style={{ marginBottom: 16, padding: '5px 12px' }}>
            <Ic.mail size={12} />
            ZAPROSZENIE
          </Pill>
          <h1
            className="serif"
            style={{
              fontSize: 36,
              fontWeight: 700,
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {isRoomInvite ? (
              <>
                Dołącz do pokoju
                <br />
                <em style={{ color: 'var(--accent)', fontStyle: 'normal' }}>{inv.roomName}</em>
              </>
            ) : (
              'Dołącz do WCTyper'
            )}
          </h1>
          <p style={{ fontSize: 15, color: '#5b6578', marginTop: 14, lineHeight: 1.5 }}>
            Obstawiajcie razem wyniki Mistrzostw Świata 2026. Zero opłat, tylko zdrowa rywalizacja.
          </p>
        </div>

        <Card padding={24} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em', marginBottom: 8 }}>
            KOD ZAPROSZENIA
          </div>
          <div
            className="mono"
            style={{
              height: 48,
              borderRadius: 10,
              border: '1.5px dashed rgba(15,30,61,.18)',
              background: '#f7f6f2',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '.15em',
            }}
          >
            {inv.code}
            <span style={{ fontSize: 11, color: '#5b6578', letterSpacing: '.04em', fontFamily: 'Geist' }}>
              {expired ? 'wygasł' : used ? 'wykorzystany' : 'aktywny'}
            </span>
          </div>
        </Card>

        {(expired || used) && !alreadyMember && (
          <div
            style={{
              padding: 14,
              background: 'rgba(208,70,60,.08)',
              color: '#8a2a22',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            To zaproszenie nie jest już aktywne.
          </div>
        )}

        {alreadyMember && (
          <>
            <div
              style={{
                padding: 14,
                background: 'rgba(31,157,85,.1)',
                color: '#0f6b34',
                borderRadius: 10,
                fontSize: 13,
                marginBottom: 16,
                lineHeight: 1.5,
              }}
            >
              Już należysz do pokoju <b>{inv.roomName}</b>. Nie musisz akceptować zaproszenia ponownie.
            </div>
            <Btn
              variant="accent"
              size="lg"
              fullWidth
              onClick={() => navigate(`/rooms/${inv.roomId}`)}
              style={{ marginBottom: 10 }}
            >
              Otwórz pokój
            </Btn>
            <Btn variant="ghost" size="lg" fullWidth onClick={() => navigate('/')}>
              Wróć na dashboard
            </Btn>
          </>
        )}

        {!expired && !used && !alreadyMember && (
          <>
            {isRoomInvite ? (
              token ? (
                <>
                  <Btn
                    variant="accent"
                    size="lg"
                    fullWidth
                    onClick={() => accept.mutate()}
                    disabled={accept.isPending}
                    style={{ marginBottom: 10 }}
                  >
                    {accept.isPending ? 'Dołączanie…' : 'Akceptuj i dołącz'}
                  </Btn>
                  <Btn variant="ghost" size="lg" fullWidth onClick={() => navigate('/')}>
                    Wróć na dashboard
                  </Btn>
                </>
              ) : (
                <>
                  <Btn
                    variant="accent"
                    size="lg"
                    fullWidth
                    onClick={() =>
                      navigate('/login', { state: { from: `/invite/${code}` } })
                    }
                    style={{ marginBottom: 10 }}
                  >
                    Zaloguj się i dołącz
                  </Btn>
                  <Btn variant="ghost" size="lg" fullWidth onClick={() => navigate(`/register?invite=${code}`)}>
                    Nie mam konta — załóż
                  </Btn>
                </>
              )
            ) : (
              <Btn
                variant="accent"
                size="lg"
                fullWidth
                onClick={() => navigate(`/register?invite=${code}`)}
              >
                Załóż konto
              </Btn>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fbfaf6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
